import { html, useEffect, useState } from '/vendor/htm-preact.module.js';
import { get, post } from '/api.js';
import { cachedGet, peek } from '/read.js';
import { Icon } from '/icons.js';

/**
 * Finding somewhere to play, and asking to be let in.
 *
 * Distance is worked out HERE, in the browser. The server hands back listed
 * places with their coordinates and is never told where you are, so there is
 * no position for it to store, leak, or later have to delete. Refusing the
 * location prompt costs you the ordering and nothing else.
 */
const R_KM = 6371;

function km(a, b) {
  const rad = (d) => (d * Math.PI) / 180;
  const dLat = rad(b.lat - a.lat);
  const dLon = rad(b.lon - a.lon);
  const h = Math.sin(dLat / 2) ** 2
    + Math.cos(rad(a.lat)) * Math.cos(rad(b.lat)) * Math.sin(dLon / 2) ** 2;
  return 2 * R_KM * Math.asin(Math.sqrt(h));
}

function useHere() {
  const [here, setHere] = useState(null);
  useEffect(() => {
    if (!navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition(
      (pos) => setHere({ lat: pos.coords.latitude, lon: pos.coords.longitude }),
      // Refused, or unavailable. Not an error worth showing: the list still
      // works, it just is not sorted by how far away things are.
      () => setHere(null),
      { timeout: 8000 });
  }, []);
  return here;
}

/** What is already known for this kind, or null. */
function knownFor(path, pick) {
  const all = peek(path);
  return all === undefined ? null : pick(all);
}

export function Discover({ kind, onChanged }) {
  const [found, setFound] = useState(() => knownFor(
    '/api/discover', (all) => (kind === 'venue' ? all.venues : all.circles)));
  const [asked, setAsked] = useState({});
  const [error, setError] = useState('');
  const here = useHere();

  async function reload() {
    const all = await cachedGet('/api/discover');
    setFound(kind === 'venue' ? all.venues : all.circles);
  }
  useEffect(() => { reload().catch((e) => setError(e.detail || e.message)); },
            [kind]);

  if (found === null) return null;
  if (found.length === 0) return null;      // nothing listed: say nothing

  // Things with a location first, nearest first; the rest keep their order.
  // A circle with no venue has no location, which is deliberate — nobody
  // publishes a home address to run a circle from their front room.
  const withDistance = found.map((f) => ({
    ...f,
    km: (here && f.lat != null && f.lon != null) ? km(here, f) : null,
  }));
  withDistance.sort((a, b) => (a.km == null) - (b.km == null)
                              || (a.km ?? 0) - (b.km ?? 0));

  async function apply(f) {
    setError('');
    const id = kind === 'venue' ? f.venue_id : f.circle_id;
    try {
      await post('/api/requests', { target_kind: kind, target_id: id });
      setAsked((a) => ({ ...a, [id]: true }));
      if (onChanged) await onChanged();
    } catch (e) { setError(e.detail || e.message); }
  }

  return html`
    <div data-testid=${`discover-${kind}`}>
      <label>${kind === 'venue' ? 'Venues near you' : 'Circles you could join'}</label>
      ${error && html`<p class="sub" data-testid="discover-error"
                         style="color:var(--danger)">${error}</p>`}
      ${withDistance.map((f) => {
        const id = kind === 'venue' ? f.venue_id : f.circle_id;
        return html`
          <div key=${id} class="card rowbetween"
               data-testid=${`discover-item-${id}`}>
            <span style="display:flex;align-items:center;gap:.6rem">
              <${Icon} name="map-pin" size=${16} />
              <span>
                ${f.name}
                ${f.venue_name && html`<span class="sub"> · ${f.venue_name}</span>`}
                ${f.km != null && html`
                  <span class="sub" data-testid="discover-distance">
                    · ${f.km < 1 ? `${Math.round(f.km * 1000)} m`
                                 : `${f.km.toFixed(1)} km`} away</span>`}
              </span>
            </span>
            ${asked[id]
              ? html`<span class="sub" data-testid="discover-asked">Asked</span>`
              : html`<button class="secondary" data-testid="discover-apply"
                             onClick=${() => apply(f)}>Ask to join</button>`}
          </div>`;
      })}
    </div>`;
}

/** Requests you are able to answer — the same question as who may change who
 *  is in that thing, so no new capability decides it. */
export function Requests({ kind, onChanged }) {
  const [rows, setRows] = useState(() => knownFor(
    '/api/requests', (all) => all.filter((r) => r.target_kind === kind)) || []);
  const [error, setError] = useState('');

  // Filtered by kind: /api/requests returns everything you may answer, and
  // without this the Circles tab showed applications to join pubs and the
  // Venues tab showed applications to join circles — the same request in both.
  async function reload() {
    const all = await cachedGet('/api/requests');
    setRows(all.filter((r) => r.target_kind === kind));
  }
  useEffect(() => { reload().catch(() => setRows([])); }, [kind]);

  async function answer(id, accept) {
    setError('');
    try {
      await post(`/api/requests/${encodeURIComponent(id)}/`
                 + (accept ? 'accept' : 'deny'));
      await reload();
      if (onChanged) await onChanged();
    } catch (e) { setError(e.detail || e.message); }
  }

  if (!rows.length) return null;

  // TWO DIRECTIONS IN ONE LIST -- GitHub issue #63. Both are "a membership
  // decision waiting on you", which is what this list is for, but they read
  // in opposite directions: one is somebody asking to come in, the other is
  // somebody asking you in. Same row, different words and different verbs,
  // because "Accept / Deny" against your own invitation reads as though you
  // were judging yourself.
  const invitations = rows.filter((r) => r.invited);
  const asking = rows.filter((r) => !r.invited);

  return html`
    <div data-testid="requests">
      ${error && html`<p class="sub" style="color:var(--danger)">${error}</p>`}

      ${invitations.length > 0 && html`
        <label>You have been asked to join</label>
        ${invitations.map((r) => html`
          <div key=${r.request_id} class="card rowbetween"
               data-testid=${`invitation-${r.request_id}`}>
            <span>${r.target_name}</span>
            <span>
              <button class="secondary" data-testid="invitation-accept"
                      onClick=${() => answer(r.request_id, true)}>
                Join
              </button>
              <button class="secondary" data-testid="invitation-decline"
                      onClick=${() => answer(r.request_id, false)}>
                No thanks
              </button>
            </span>
          </div>`)}`}

      ${asking.length > 0 && html`
        <label>Asking to join</label>
        ${asking.map((r) => html`
          <div key=${r.request_id} class="card rowbetween"
               data-testid=${`request-${r.request_id}`}>
            <span>${r.name} <span class="sub">· ${r.target_name}</span></span>
            <span>
              <button class="secondary" data-testid="request-accept"
                      onClick=${() => answer(r.request_id, true)}>Accept</button>
              <button class="secondary" data-testid="request-deny"
                      onClick=${() => answer(r.request_id, false)}>Deny</button>
            </span>
          </div>`)}`}
    </div>`;
}
